import { useState, useCallback } from 'react';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { SnackType } from '@models/common.model';
import { PendingApproval } from '@models/task.model';
import TasksGraphQL from '@graphql/task.queries';
import { graphQlClient } from '@config/graphqlClient';

export interface UseTaskApprovalActionsResult {
  loadingTaskId: string | null;
  rejectTarget: PendingApproval | null;
  handleApprove: (approval: PendingApproval) => Promise<void>;
  handleReject: (approval: PendingApproval) => void;
  confirmReject: (reason: string) => Promise<void>;
  closeRejectModal: () => void;
}

/**
 * Sprint 16, story-075/076. Approve/Reject orchestration extracted verbatim from
 * `ApprovalsNotification/index.tsx` (deleted this sprint along with the clipped sidebar panel it
 * powered) so `NeedsYourActionBlock` can drive the identical behavior without re-implementing it.
 * Approve calls `approveTask(id)`: the server decides what approving means for this task, so this
 * hook never picks a target status. (It used to route on `kind` alone — `forApprovalTask` for
 * INTERNAL_APPROVAL — which sent an in-house task to CLIENT_APPROVAL and wrote no feed activity.)
 * Reject reuses `RejectionModal` unmodified (required note, `rejectTask(id, note)`); the note is
 * recorded as the status-change reason shown in the activity feed.
 *
 * `refetch` is the caller's own `usePendingApprovals` refetch — `dashboard.tsx` mounts the query
 * (moved up from where `ApprovalsNotification` used to own it), this hook only orchestrates the
 * mutations and calls the refetch it's given, matching today's behavior exactly (a direct
 * `.refetch()`, never `queryClient.invalidateQueries`).
 */
export const useTaskApprovalActions = (
  refetch: () => void,
): UseTaskApprovalActionsResult => {
  const dispatch = useAppDispatch();
  const [loadingTaskId, setLoadingTaskId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingApproval | null>(null);

  const handleApprove = useCallback(
    async (approval: PendingApproval) => {
      setLoadingTaskId(approval.taskId);
      try {
        await graphQlClient.request(TasksGraphQL.mutations.approveTask, { id: approval.taskId });
        dispatch(
          appendActionMessage({
            message:
              approval.approveOutcome === 'CLIENT_APPROVAL'
                ? 'Task approved and sent to the client'
                : 'Task approved successfully',
            type: SnackType.SUCCESS,
          }),
        );
        refetch();
      } catch (e: any) {
        dispatch(
          appendActionMessage({
            message: e?.response?.errors?.[0]?.message || 'Error approving task',
            type: SnackType.ERROR,
          }),
        );
      } finally {
        setLoadingTaskId(null);
      }
    },
    [dispatch, refetch],
  );

  const handleReject = useCallback((approval: PendingApproval) => {
    setRejectTarget(approval);
  }, []);

  const confirmReject = useCallback(
    async (reason: string) => {
      if (!rejectTarget) return;
      setLoadingTaskId(rejectTarget.taskId);
      try {
        await graphQlClient.request(TasksGraphQL.mutations.rejectTask, {
          id: rejectTarget.taskId,
          note: reason,
        });
        dispatch(
          appendActionMessage({ message: 'Task rejected successfully', type: SnackType.SUCCESS }),
        );
        refetch();
      } catch (e: any) {
        dispatch(
          appendActionMessage({
            message: e?.response?.errors?.[0]?.message || 'Error rejecting task',
            type: SnackType.ERROR,
          }),
        );
      } finally {
        setLoadingTaskId(null);
        setRejectTarget(null);
      }
    },
    [rejectTarget, dispatch, refetch],
  );

  const closeRejectModal = useCallback(() => setRejectTarget(null), []);

  return {
    loadingTaskId,
    rejectTarget,
    handleApprove,
    handleReject,
    confirmReject,
    closeRejectModal,
  };
};

export default useTaskApprovalActions;
